import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'
import { ORGANIZATION_ACTIONS } from '../events/orgEvents'
import { START_TRIAL } from '../graphql/billing'
import { QUERY_ACCOUNT } from '../graphql/account'
import type { Account } from '../types'

export type UserStartTrialParams = {
  user?: Account
  attribution?: {
    cta: string
    upgradePathName: string
  }
}

export type StartTrialPayload =
  | { __typename: 'StartTrialResponse' }
  | { __typename: 'VoidMutationError'; message: string }

export type UserStartTrialReturn = {
  startTrial: () => void
  trial: StartTrialPayload | null
  error: ErrorLike | { message: string } | null
  processing: boolean
}

const useStartTrial = ({
  user,
  attribution,
}: UserStartTrialParams): UserStartTrialReturn => {
  const [startTrial, { data, error: mutationError }] = useMutation(
    START_TRIAL,
    {
      refetchQueries: [{ query: QUERY_ACCOUNT }],
      awaitRefetchQueries: true,
      onCompleted: () => {
        ORGANIZATION_ACTIONS.billingUpdated({ user })
      },
    },
  )
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<ErrorLike | { message: string } | null>(
    null,
  )

  const trial: StartTrialPayload | null = data?.startTrial ?? null

  useEffect(() => {
    if (processing && user?.currentOrganization?.id) {
      startTrial({
        variables: {
          input: {
            organizationId: user.currentOrganization.id,
            attribution,
          },
        },
      }).catch((e) => {
        console.error(e) // eslint-disable-line no-console
      })
    }
  }, [processing])

  useEffect(() => {
    if (mutationError) {
      setError(mutationError)
      setProcessing(false)
    } else if (trial?.__typename === 'VoidMutationError') {
      setError({ message: trial.message })
      setProcessing(false)
    }
  }, [trial, mutationError])

  return {
    startTrial: (): void => {
      setProcessing(true)
    },
    trial,
    error,
    processing,
  }
}

export default useStartTrial
